/* eslint-disable camelcase */
import { createAction } from '@reduxjs/toolkit';
import { AppDispatch, AppState } from 'app/store';
import {
  API_ENDPOINTS,
  PANGO_REDEEM_AUTHORIZATION_TOKEN,
  STORAGE_KEYS,
} from 'utils/constants';
import { setInStorage, logErrors } from 'utils';
import { LooseObjectType } from 'utils/globalTypes';
import { InitialValues } from 'pages/signin';
import { deleteAPI, postAPI } from 'services/api/apiAsync';
import axios from 'axios';
import formatEntitlements, {
  formatUserFeatures,
  getAgentIdFromAccessToken,
  getXsrfTokenFromJwt,
  getSessionIdFromAccessToken,
} from 'services/session/utils';
import { doLockVault } from 'services/vault/vaultAsync';
import {
  selectBrandCode,
  selectBrandVaultProject,
} from 'services/brand/brandSelectors';
import { setBrand } from 'services/brand/brandSlice';
import type { SessionSliceType } from './types';
import { setSessionSlice, refreshTokenUpdate } from './sessionSlice';
import { selectRefreshToken, selectToken } from './sessionSelectors';

const signOutRequest = createAction('SIGNOUT_REQUEST');

export function storeUserSessionDetails(response: LooseObjectType) {
  return async (dispatch: AppDispatch) => {
    try {
      const { token, accessToken, refreshToken, profile } = response;
      const { missingPii } = profile;
      const xsrfToken = getXsrfTokenFromJwt(token);
      const entitlements = formatEntitlements(
        accessToken,
      ) as SessionSliceType['entitlements'];
      const features = formatUserFeatures(entitlements);
      const agentId = getAgentIdFromAccessToken(accessToken);
      const sessionId = getSessionIdFromAccessToken(accessToken);

      // update session and entitlements
      dispatch(
        setSessionSlice({
          token: xsrfToken,
          userLoggedIn: true,
          agentId,
          entitlements,
          accessToken,
          refreshToken,
          features,
          missingPii,
          sessionId,
        }),
      );
      return undefined;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// user signin
export function createUserSession(values: InitialValues) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.userSession,
        data: {
          username: values.username,
          password: values.password,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();

      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// clear slice data on sign out
export function clearStore() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    const { brand } = getState();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { companyDetails, ...brandDetails } = brand;
    dispatch(signOutRequest()); // To reset the all the slice state to initial values
    dispatch(setBrand(brandDetails)); // To set the brand details
  };
}

// user sign out
export function clearSession() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const refreshToken = selectRefreshToken(store);
      const token = selectToken(store);
      const config = {
        url: API_ENDPOINTS.userSession,
        headers: {
          'X-XSRF-TOKEN': token,
          REFRESH_TOKEN: refreshToken,
        },
      };
      try {
        dispatch(doLockVault());
      } catch (error) {
        logErrors(error);
      }
      try {
        await dispatch(deleteAPI(config)).unwrap();
        dispatch(clearStore());
      } catch (error) {
        dispatch(clearStore());
      }
      return null;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Makes POST request to /client/sessions/refresh to refresh the access token
 * Dispatches action REFRESH_TOKEN on success
 */
export function refreshApToken() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const refreshToken = selectRefreshToken(store);
      const token = selectToken(store);
      const config = {
        url: API_ENDPOINTS.refreshToken,
        data: {
          refreshToken,
        },
        headers: {
          'X-XSRF-TOKEN': token,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      dispatch(refreshTokenUpdate(response));

      return response;
    } catch (error) {
      if (error === 401) {
        setInStorage(STORAGE_KEYS.sessionExpired, true);
        dispatch(clearStore());
      }
      return Promise.reject(error);
    }
  };
}

export function verifySignInTokenAndGetSessionTokens(token: string) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const brandCode = selectBrandCode(store);
      const brandVaultProject = selectBrandVaultProject(store);
      const config = {
        url: API_ENDPOINTS.pangoBaseURL + API_ENDPOINTS.redeemSession,
        method: 'post',
        data: {
          signin_token: token,
        },
        headers: {
          'Content-Type': 'application/json',
          'X-Idg-Brand': brandCode,
          'x-project': brandVaultProject,
          Authorization: PANGO_REDEEM_AUTHORIZATION_TOKEN,
        },
      };

      const response = await axios(config);
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// Masquerade user session
export function masqueradeUserSession(token: string) {
  return async (dispatch: AppDispatch): Promise<LooseObjectType> => {
    try {
      const response = await dispatch(
        verifySignInTokenAndGetSessionTokens(token),
      );

      return {
        token,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        profile: {
          missingPii: [],
        },
      };
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/* Function to fetch ChatAgent scoped JWT token */
export function fetchThinSessionDetails() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.thinSession,
      };

      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
